import React, { useState } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Image from "../../../components/AppImage";
import DecryptedImage from "../../../components/DecryptedImage";
import ImageSourceDropdown from "./ImageSourceDropdown";

const DocumentPhotosForm = ({
  aadharPhoto,
  bankPassbookPhoto,
  electricityBillPhoto,
  onAadharPhotoChange,
  onBankPassbookPhotoChange,
  onElectricityBillPhotoChange,
  errors,
}) => {
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [selectedDocumentType, setSelectedDocumentType] = useState(null);
  const [processingDoc, setProcessingDoc] = useState(null);

  const handleFileChange = async (file, documentType, onChange) => {
    if (!file) return;
    setProcessingDoc(documentType);
    try {
      onChange(file);
    } finally {
      setProcessingDoc(null);
    }
  };

  const removePhoto = (onChange) => {
    onChange(null);
  };

  const DocumentPhotoSection = ({
    title,
    photo,
    onChange,
    documentType,
    icon,
  }) => {
    const inputId = `document-${documentType}`;
    const photoUrl = photo instanceof File ? URL.createObjectURL(photo) : photo;
    const isProcessing = processingDoc === documentType;

    return (
      <div className="space-y-3 p-4 rounded-lg border border-border bg-muted/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon name={icon} size={18} className="text-primary" />
            <label className="text-sm font-semibold text-foreground">
              {title}
            </label>
          </div>
        </div>

        {!photo ? (
          <ImageSourceDropdown
            idPrefix={inputId}
            triggerLabel={`Add ${title} photo`}
            triggerSubLabel="Upload or capture"
            processing={isProcessing}
            onFileSelect={(file) => handleFileChange(file, documentType, onChange)}
          />
        ) : (
          <div className="relative rounded-lg overflow-hidden border border-border bg-muted">
            <div className="aspect-[4/3] overflow-hidden">
              <DecryptedImage
                src={photoUrl}
                alt={title}
                className="w-full h-full object-contain"
              />
            </div>
            <div className="absolute inset-0 bg-black/50 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                iconName="Eye"
                onClick={() => {
                  setSelectedPhoto(photo);
                  setSelectedDocumentType(title);
                }}
              >
                View
              </Button>
              <Button
                variant="destructive"
                size="sm"
                iconName="Trash2"
                onClick={() => removePhoto(onChange)}
              >
                Remove
              </Button>
            </div>
          </div>
        )}

        {errors?.[documentType] && (
          <p className="text-sm text-error">{errors[documentType]}</p>
        )}
      </div>
    );
  };

  return (
    <div className="bg-card rounded-lg border border-border p-4 md:p-6 lg:p-8">
      <div className="flex items-center gap-3 mb-4 md:mb-6">
        <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
          <Icon name="FileText" size={20} className="text-secondary" />
        </div>
        <h2 className="text-xl md:text-2xl font-semibold text-foreground">
          Document Photos
        </h2>
      </div>

      <div className="space-y-4">
        <DocumentPhotoSection
          title="Aadhar Card"
          photo={aadharPhoto}
          onChange={onAadharPhotoChange}
          documentType="aadhar"
          icon="CreditCard"
        />

        <DocumentPhotoSection
          title="Bank Passbook"
          photo={bankPassbookPhoto}
          onChange={onBankPassbookPhotoChange}
          documentType="bank"
          icon="Wallet"
        />

        <DocumentPhotoSection
          title="Electricity Bill"
          photo={electricityBillPhoto}
          onChange={onElectricityBillPhotoChange}
          documentType="bill"
          icon="FileText"
        />
      </div>

      {errors?.documentPhotos && (
        <p className="text-sm text-error mt-4">{errors.documentPhotos}</p>
      )}

      {selectedPhoto && (
        <div
          className="fixed inset-0 bg-black/80 z-[2000] flex items-center justify-center p-4"
          onClick={() => {
            setSelectedPhoto(null);
            setSelectedDocumentType(null);
          }}
        >
          <div className="relative max-w-4xl w-full">
            <Button
              variant="secondary"
              size="icon"
              iconName="X"
              onClick={() => {
                setSelectedPhoto(null);
                setSelectedDocumentType(null);
              }}
              className="absolute -top-12 right-0"
            />
            <div className="mb-2 text-white text-center">
              <p className="text-sm font-medium">{selectedDocumentType}</p>
            </div>
            <DecryptedImage
              src={
                selectedPhoto instanceof File
                  ? URL.createObjectURL(selectedPhoto)
                  : selectedPhoto
              }
              alt={`Full size ${selectedDocumentType} photo`}
              className="w-full h-auto rounded-lg"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentPhotosForm;
